import { useEffect } from "react";
import { Bell, X, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n";
import type { WaiterCall } from "@/hooks/use-waiter-calls";

function formatTime(ts: number) {
  const d = new Date(ts);
  return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function timeAgo(ts: number) {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60) return `${diff}sn once`;
  if (diff < 3600) return `${Math.floor(diff / 60)}dk once`;
  return formatTime(ts);
}

export function WaiterCallPanel({
  calls,
  onDismiss,
  onDismissAll,
  onRequestPermission,
}: {
  calls: WaiterCall[];
  onDismiss: (id: string) => void;
  onDismissAll: () => void;
  onRequestPermission: () => void;
}) {
  const { t } = useI18n();

  // Request notification permission on mount
  useEffect(() => {
    onRequestPermission();
  }, [onRequestPermission]);

  if (calls.length === 0) return null;

  return (
    <div className="w-80">
      <div className="rounded-xl border bg-card shadow-2xl overflow-hidden animate-in slide-in-from-right">
        {/* Header */}
        <div className="flex items-center justify-between bg-primary px-4 py-3 text-primary-foreground">
          <div className="flex items-center gap-2">
            <Bell className="size-4 animate-bounce" />
            <span className="font-semibold text-sm">
              {t("waiter_call")} ({calls.length})
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-primary-foreground hover:bg-white/20"
            onClick={onDismissAll}
          >
            <CheckCheck className="mr-1 size-3" />
            {t("all")}
          </Button>
        </div>

        {/* Calls */}
        <div className="max-h-64 overflow-y-auto divide-y">
          {calls.map((call) => (
            <div
              key={call.id}
              className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
            >
              <div>
                <div className="font-semibold text-sm">{call.table}</div>
                <div className="text-xs text-muted-foreground">
                  {call.venueName} · {timeAgo(call.timestamp)}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 shrink-0"
                onClick={() => onDismiss(call.id)}
              >
                <X className="size-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
